import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  HeartPulse,
  Leaf,
  MoonStar,
  Sparkles,
  Utensils,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";

const CTA_URL =
  "https://form.typeform.com/to/Gx3oVrwN?typeform-source=shyrabhalla.com";

const familiarList = [
  "You eat relatively healthy at times, but then fall back into phases where everything feels off track",
  "Your weight hasn’t moved in months, no matter what you try",
  "You feel bloated, inflamed, or uncomfortable after meals",
  "Your energy crashes during the day",
  "You want to start a proper plan, but feel like your routine is too busy to stick with it",
  "You try to start new routines, but struggle to stick with them long term",
];

const focusAreas = [
  { icon: Leaf, label: "Digestion" },
  { icon: Sparkles, label: "Energy levels" },
  { icon: MoonStar, label: "Hormonal balance" },
  { icon: HeartPulse, label: "Daily routines" },
  { icon: Utensils, label: "Eating patterns" },
  { icon: Circle, label: "Stress and recovery" },
];

const programInclusions = [
  "Deep onboarding and full health history review",
  "Nutrition plan fully personalized to your current health status, goals, and daily routine (updated monthly)",
  "Supplement strategy tailored to your goals",
  "Movement guidance and wellness habit-building",
  "Weekly check-in calls",
  "Monthly progress review sessions",
  "WhatsApp access for questions and adjustments",
  "Progress tracking through symptoms, energy, digestion, and measurements",
  "A holistic strategy addressing fatigue, bloating, cravings, skin, and hormonal balance",
];

const bestFor = [
  "Want to improve both body composition and overall health",
  "Feel stuck despite trying to eat “healthy”",
  "Want a structured, personalized approach",
  "Are ready to commit to 3 months of change",
];

const notFor = ["Crash diets", "Detoxes", "Quick fixes"];

const expectedResults = [
  "Fat loss, muscle gain, and body recomposition",
  "Improved digestion and reduced bloating",
  "More stable energy",
  "Fewer cravings",
  "Better relationship with food",
  "A routine you can sustain long term",
];

const sectionVariants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.32,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.02,
    },
  },
};

export default function ProgramPage() {
  return (
    <div className="relative overflow-x-hidden bg-reset-cream pt-20 text-reset-mocha" data-testid="program-page-root">
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar />

      <header
        className="relative isolate min-h-[95vh]"
        data-testid="hero-section-container"
      >
        <img
          src="https://images.unsplash.com/photo-1617105246082-6392e241dfda?auto=format&fit=crop&w=1920&q=70"
          alt="Soft beige abstract texture"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FDFBF7F2] via-[#FDFBF7FC] to-[#F5F0E6F0]" />

        <motion.div
          className="mx-auto flex min-h-[95vh] w-full max-w-6xl flex-col justify-center px-6 py-20 md:px-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="mb-7 w-fit rounded-2xl border border-[#D4C5B0] bg-[#FDFBF7]/80 px-4 py-3"
            data-testid="hero-eyebrow-text"
          >
            <p className="font-clean text-xl font-bold tracking-[0.04em] text-[#3F322A]" data-testid="hero-name-text">
              SHYRA BHALLA
            </p>
            <p
              className="mt-1 text-sm text-[#6B5A4E]"
              data-testid="hero-credentials-text"
            >
              Nutritionist, health coach, weight management specialist
            </p>
          </div>
          <h1
            className="max-w-4xl font-heading text-4xl leading-[1.1] text-[#4A3B32] sm:text-5xl lg:text-6xl"
            data-testid="hero-main-heading"
          >
            A 12-Week Body &amp; Health Reset
          </h1>
          <p
            className="mt-7 max-w-3xl text-base leading-relaxed text-[#5B4A3F] md:text-lg"
            data-testid="hero-subheading-text"
          >
            A personalized, 1-on-1 nutrition and lifestyle program designed to
            reset your metabolism, improve digestion, balance hormones, and help
            your body finally start working with you again.
          </p>

          <div
            className="mt-10 flex flex-wrap items-center gap-4"
            data-testid="hero-cta-group"
          >
            <Button
              asChild
              className="h-auto rounded-full bg-[#4A3B32] px-8 py-4 text-base text-[#FDFBF7] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#6B5A4E]"
            >
              <a
                href={CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-apply-for-coaching-button"
              >
                Apply <ArrowRight aria-hidden="true" />
              </a>
            </Button>
          </div>
        </motion.div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-col gap-16 px-6 py-16 md:gap-24 md:px-12 md:py-24">
        <motion.section
          className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-14"
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.01 }}
          data-testid="familiar-section"
        >
          <div className="md:col-span-7">
            <p
              className="mb-3 text-xs uppercase tracking-[0.2em] text-[#7A6658]"
              data-testid="familiar-section-eyebrow"
            >
              Common signs
            </p>
            <h2
              className="font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl"
              data-testid="familiar-section-title"
            >
              Does this sound familiar?
            </h2>

            <ul className="mt-8 space-y-4" data-testid="familiar-list">
              {familiarList.map((item, index) => (
                <li
                  key={item}
                  className="flex items-start gap-3 rounded-2xl border border-[#E7DDCF] bg-white/60 p-4 text-sm leading-relaxed text-[#5B4A3F] md:text-base"
                  data-testid={`familiar-list-item-${index + 1}`}
                >
                  <Leaf
                    className="mt-0.5 h-4 w-4 shrink-0 text-[#8B7355]"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <p
              className="mt-8 text-base leading-relaxed text-[#4A3B32] md:text-lg"
              data-testid="familiar-section-closing-copy"
            >
              Most people assume the solution is more discipline. But in reality,
              the issue is usually a strategy that doesn’t actually match your
              body and lifestyle.
            </p>
          </div>

          <div
            className="relative overflow-hidden rounded-[2rem] border border-[#E0D6C8] md:col-span-5"
            data-testid="familiar-section-image-container"
          >
            <img
              src="https://images.unsplash.com/photo-1604176132453-922aeee365df?auto=format&fit=crop&w=1280&q=70"
              alt="Healthy meal planning and mindful food choices"
              className="h-full min-h-[420px] w-full object-cover object-center"
              data-testid="familiar-section-image"
              loading="lazy"
              decoding="async"
            />
          </div>
        </motion.section>

        <motion.section
          className="rounded-[2rem] border border-[#E7DDCF] bg-[#FBF7F0] p-8 md:p-12"
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.01 }}
          data-testid="program-not-diet-section"
        >
          <p
            className="text-xs uppercase tracking-[0.2em] text-[#7A6658]"
            data-testid="program-not-diet-eyebrow"
          >
            The approach
          </p>
          <h2
            className="mt-3 max-w-3xl font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl"
            data-testid="program-not-diet-title"
          >
            This program is not another diet.
          </h2>
          <p
            className="mt-5 max-w-4xl text-base leading-relaxed text-[#5B4A3F] md:text-lg"
            data-testid="program-not-diet-description"
          >
            It’s a structured 3-month reset where we rebuild how your body
            responds to food, routine, and stress.
          </p>

          <div
            className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="focus-areas-grid"
          >
            {focusAreas.map(({ icon: Icon, label }, index) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-2xl border border-[#E0D6C8] bg-white/70 px-4 py-4"
                data-testid={`focus-area-item-${index + 1}`}
              >
                <Icon className="h-5 w-5 text-[#8B7355]" aria-hidden="true" />
                <span className="text-sm text-[#4A3B32] md:text-base">{label}</span>
              </div>
            ))}
          </div>

          <p
            className="mt-8 text-base leading-relaxed text-[#4A3B32] md:text-lg"
            data-testid="program-not-diet-closing-copy"
          >
            From there, I create a completely personalized framework so your
            body finally starts responding again.
          </p>
        </motion.section>

        <motion.section
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.01 }}
          data-testid="inside-reset-section"
        >
          <p
            className="text-xs uppercase tracking-[0.2em] text-[#7A6658]"
            data-testid="inside-reset-eyebrow"
          >
            What’s included
          </p>
          <h2
            className="mt-3 font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl"
            data-testid="inside-reset-title"
          >
            Inside the 3-Month Reset
          </h2>

          <div
            className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3"
            data-testid="inside-reset-grid"
          >
            {programInclusions.map((item, index) => (
              <article
                key={item}
                className="flex items-start gap-3 rounded-2xl border border-[#E0D6C8] bg-white/70 px-4 py-4"
                data-testid={`inside-reset-item-${index + 1}`}
              >
                <p className="text-sm leading-relaxed text-[#4A3B32] md:text-base">{item}</p>
              </article>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="grid grid-cols-1 gap-6 md:grid-cols-2"
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.01 }}
          data-testid="best-fit-section"
        >
          <div className="md:col-span-2" data-testid="best-fit-section-heading-wrap">
            <h2
              className="font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl"
              data-testid="best-fit-section-heading"
            >
              Who this program is for
            </h2>
          </div>

          <article
            className="rounded-[1.75rem] border border-[#D9CAB6] bg-[#F7F0E6] p-7 md:p-8"
            data-testid="best-fit-positive-card"
          >
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#6B5A4E]"
              data-testid="best-fit-positive-eyebrow"
            >
              This program is best suited for people who:
            </p>
            <ul className="mt-5 space-y-3" data-testid="best-fit-positive-list">
              {bestFor.map((item, index) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-sm text-[#4F4036] md:text-base"
                  data-testid={`best-fit-positive-item-${index + 1}`}
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <article
            className="rounded-[1.75rem] border border-[#D1BDA5] bg-[#EFE4D6] p-7 md:p-8"
            data-testid="best-fit-negative-card"
          >
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#6F594A]"
              data-testid="best-fit-negative-eyebrow"
            >
              This is not for people looking for
            </p>
            <ul className="mt-5 space-y-3" data-testid="best-fit-negative-list">
              {notFor.map((item, index) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-sm text-[#5B473C] md:text-base"
                  data-testid={`best-fit-negative-item-${index + 1}`}
                >
                  <Circle className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        </motion.section>

        <motion.section
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.01 }}
          data-testid="expected-results-section"
        >
          <p
            className="text-xs uppercase tracking-[0.2em] text-[#7A6658]"
            data-testid="expected-results-eyebrow"
          >
            Expected Results
          </p>
          <h2
            className="mt-3 font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl"
            data-testid="expected-results-title"
          >
            Clients typically experience:
          </h2>

          <div
            className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="expected-results-grid"
          >
            {expectedResults.map((item, index) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-2xl border border-[#E0D6C8] bg-white/70 px-4 py-4"
                data-testid={`expected-results-item-${index + 1}`}
              >
                <p className="text-sm leading-relaxed text-[#4A3B32] md:text-base">{item}</p>
              </div>
            ))}
          </div>

          <div className="mt-10" data-testid="expected-results-testimonials-cta-wrap">
            <Button
              asChild
              className="h-auto rounded-full bg-[#4A3B32] px-8 py-4 text-base text-[#FDFBF7] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#6B5A4E]"
            >
              <Link to="/#testimonials" data-testid="see-client-testimonials-button">
                See what my clients say <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </motion.section>
      </main>

      <footer
        className="mx-6 mb-8 rounded-[2rem] bg-[#4A3B32] px-6 py-16 text-[#FDFBF7] md:mx-12 md:px-12"
        data-testid="final-cta-section"
      >
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
          <h2
            className="max-w-3xl font-heading text-3xl leading-tight md:text-4xl"
            data-testid="final-cta-title"
          >
            If you’re serious about your health, apply here.
          </h2>
          <div className="pt-2" data-testid="final-cta-button-wrap">
            <Button
              asChild
              className="h-auto rounded-full bg-[#FDFBF7] px-8 py-4 text-base text-[#4A3B32] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#EFE6D8]"
            >
              <a
                href={CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-apply-for-coaching-button"
              >
                Apply for Coaching <ArrowRight aria-hidden="true" />
              </a>
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
